<script setup lang="ts">
import PanelGroup from '../../PanelGroup.vue'
import CompletionFields from '../../settings/CompletionFields.vue'
import EditorTextField from '../../EditorTextField.vue'
import useBrandAppConfig from '../../../composables/useBrandAppConfig'
import { useEditorLoyaltyData } from '../../../composables/editorLoyaltyData'
import { REGISTRATION_STEP_TEXTS, type RegistrationStepId } from '../../../lib/brandAppRegistration'
import type { PanelTab } from '../../../lib/brandAppSections'

const props = defineProps<{
  step: RegistrationStepId
  tab?: PanelTab
}>()

const { t } = useI18n()
const { merchantTextsForm, webappTextDefaults } = useMerchantTexts()
const { identity } = useBrandAppConfig()
const { hasFormCompletion } = useEditorLoyaltyData()

const welcomeFields = computed(() => merchantTextsForm.value.webappTextsFields)
const welcomeDefaults = computed(() => webappTextDefaults(identity.value.usePoliteLanguage))
const registrationFields = computed(() =>
  REGISTRATION_STEP_TEXTS[props.step].map((key) => ({
    key,
    field: merchantTextsForm.value.webappRegistrationTextsFields[key]
  }))
)
const isProfileStep = computed(() => props.step === 'reg-profile')
const showContent = computed(() => isProfileStep.value && props.tab !== 'text')
const showTexts = computed(() => !isProfileStep.value || props.tab !== 'content')
</script>

<template>
  <div class="flex flex-col">
    <PanelGroup v-if="showContent">
      <BaseSwitch
        id="brandApp.registration.askCompletion"
        v-model="hasFormCompletion"
        :label="t('pages.loyaltySupports.webapp.editor.registration.askCompletion')"
        :description="t('pages.loyaltySupports.webapp.editor.registration.askCompletionDescription')"
      />
    </PanelGroup>

    <PanelGroup v-if="showContent && hasFormCompletion">
      <CompletionFields />
    </PanelGroup>

    <PanelGroup v-if="showTexts">
      <div class="flex flex-col gap-5">
        <p v-if="isProfileStep && !hasFormCompletion" class="text-foreground-secondary text-sm leading-relaxed">
          {{ t('pages.loyaltySupports.webapp.editor.registration.completionOff') }}
        </p>
        <EditorTextField
          v-if="step === 'reg-welcome'"
          id="brandApp.registration.welcomeTitle"
          v-model="welcomeFields.webappHomeTitle.value"
          :label="t('pages.loyaltySupports.webapp.editor.registration.title')"
          :max-length="welcomeFields.webappHomeTitle.maxLength ?? 75"
          :placeholder="welcomeDefaults.webappHomeTitle"
          :default-value="welcomeDefaults.webappHomeTitle"
        />
        <EditorTextField
          v-if="step === 'reg-phone'"
          id="brandApp.registration.phoneTitle"
          v-model="welcomeFields.webappHomeSubtitle.value"
          :label="t('pages.loyaltySupports.webapp.editor.registration.title')"
          :max-length="welcomeFields.webappHomeSubtitle.maxLength ?? 30"
          :placeholder="welcomeDefaults.webappHomeSubtitle"
          :default-value="welcomeDefaults.webappHomeSubtitle"
        />

        <EditorTextField
          v-for="{ key, field } in registrationFields"
          :id="`brandApp.registration.${key}`"
          :key="key"
          v-model="field.value"
          :label="field.label"
          :max-length="field.maxLength ?? 255"
          :placeholder="field.defaultValue"
          :default-value="field.defaultValue"
        />
      </div>
    </PanelGroup>
  </div>
</template>
